import { getConfig } from "./env";

/**
 * Where the public site's pages are (`APP-011`).
 *
 * This app links out to `dealbid.app` from its footer, and until this table existed it did
 * so with hand-typed paths. Two of the three named routes `dealbid-web` does not serve —
 * `/how-it-works` and `/rules` — and neither failed, because the public site is a static
 * bundle with `not_found_handling: single-page-application`: an unknown path renders the
 * homepage. A founder who asked how bidding works was dropped at the top of the board with
 * nothing to say anything had gone wrong.
 *
 * Canon `AGENTS.md` § 4: cross-repo behaviour "must be defined through `contracts/` and
 * versioned API/data contracts, not by copying assumptions between repos". A route table is
 * still a copied assumption — but a copy in one declared place is one a test can check
 * against the real site, which a path typed into an anchor is not. `webUrl` takes a name
 * from here and nothing else, so a path that is not in this table is a type error rather
 * than a link that silently lands on the board.
 *
 * The mirror is `repos/dealbid-web/src/lib/routes.ts`. Keep the names and the order the same
 * as that file's, so the two can be read side by side; `e2e/tests/cross-repo-links.spec.ts`
 * is what proves they still agree, because only the harness has both repositories running.
 */

/** Mirrors `dealbid-web`'s `ROUTE_PATHS` — the site's real routes. */
export const WEB_ROUTE_PATHS = {
  home: "/",
  /**
   * Retired as a form and kept as a forwarding address (`WEB-014`): canon decision `D-008`
   * moved submission behind a Google sign-in and into this app. It stays in the table
   * because it is still a route the public site knows, and a link here would resolve.
   */
  submit: "/submit",
  /** The Disclosure policy (`WEB-018`). */
  disclosure: "/disclosure",
} as const;

/**
 * Mirrors `dealbid-web`'s `HOME_SECTIONS` — the homepage's sections, as link targets.
 *
 * Written as a path plus fragment rather than a bare `#id` for the reason that file gives:
 * the public site carries these in its masthead on every page, so a bare fragment names
 * nothing away from the homepage. From this app, which is a different origin entirely, the
 * path is not optional at all.
 */
export const WEB_HOME_SECTIONS = {
  today: `${WEB_ROUTE_PATHS.home}#today`,
  honorable: `${WEB_ROUTE_PATHS.home}#honorable`,
  weekly: `${WEB_ROUTE_PATHS.home}#weekly`,
  monthly: `${WEB_ROUTE_PATHS.home}#monthly`,
  whatIsDealbid: `${WEB_ROUTE_PATHS.home}#what-is-dealbid`,
  tomorrow: `${WEB_ROUTE_PATHS.home}#tomorrow`,
  howItWorks: `${WEB_ROUTE_PATHS.home}#how-it-works`,
  founding: `${WEB_ROUTE_PATHS.home}#founding`,
  subscribe: `${WEB_ROUTE_PATHS.home}#subscribe`,
} as const;

/**
 * Every target this app may link to, by name.
 *
 * One flat table so a caller writes `webUrl("howItWorks")` without first deciding whether
 * what it wants is a route or a section — a distinction that belongs to the public site's
 * routing and not to a link in this app's footer.
 */
export const WEB_TARGETS = { ...WEB_ROUTE_PATHS, ...WEB_HOME_SECTIONS } as const;

/** A name in {@link WEB_TARGETS}. The only thing `webUrl` accepts. */
export type WebTarget = keyof typeof WEB_TARGETS;

/** The site-relative path a target names, always beginning with `/`. */
export function webPath(target: WebTarget): string {
  return WEB_TARGETS[target];
}

/**
 * The absolute URL of a public-site target, named rather than typed (`APP-011`).
 *
 * It takes a {@link WebTarget} and not a path, so a route `dealbid-web` does not serve
 * cannot be written at a call site at all — a typed path does not compile. That matters
 * because of the SPA fallback described above: a wrong path is not a `404`, it is a link
 * that appears to work.
 */
export function webUrl(target: WebTarget): string {
  return `${getConfig().webBaseUrl}${webPath(target)}`;
}
